import * as React from "react";
import * as ReactDOM from "react-dom";
import { Route } from "react-router-dom";
import * as utils from "../../../utils/utils";
import * as modelsGet from "../../../models/get";
import * as modelsPost from "../../../models/post";
import { IComboBoxValue } from "../../../models/form";
import * as globals from "../../../globals";
import * as apiSettings from "../../../api/settings";
import * as apiCalendars from "../../../api/calendars";
import { Window } from "../../../components/window";
import { Loader, LoaderStyle } from "../../../components/loader";
import { Button, ButtonType } from "../../../components/button";
import { ScrollViewer, ScrollBarVisibility } from "../../../components/scrollViewer";
import { FormGroup } from "../../../components/form";
import { ComboBox, ComboBoxType, DROPDOWN_TEXT_ITEM_HEIGHT, DropDownTextItem } from "../../../components/comboBox";
import { Alert, AlertType } from "../../../components/alert";
import { GroupBox } from "../../../components/groupBox";
import { InputSize } from "../../../components/input";

enum Mode
{
    create,
    edit
}

interface IProperties
{
    /**
     * Die ID des zu öffnenden Datensatzes. (Ist leer wenn eine neuer Datensatz erstellt werden soll.)
     */
    rule?: modelsGet.ITimeGroup,

    /**
     * Wird aufgerufen sobald sich das Fenster schließt.
     */
    onClose?: (refresh?: boolean) => void
}

interface IState
{
    /**
     * Gibt an, ob Daten gerade geladen werden.
     */
    isLoading?: boolean,

    /**
     * Gibt den Modus an in dem sich der Dialog befindet.
     */
    mode?: Mode,

    /**
     * Wenn im Bearbeitungsmodus, dann ist diese Eigenschaft gefüllt.
     */
    rule?: modelsGet.ITimeGroup,

    calendars?: modelsGet.ICalendar[],

    /**
     * Enthält die Eingabedaten des Forms.
     */
    form?: {
        calendar?: IComboBoxValue<string, number>,
        calendars?: number[]
    },

    /**
     * Enthält Serverseitige Fehlermeldungen.
     */
    errors?: Object,

    /**
     * Der Text der Meldungen welche angezeigt werden soll.
     */
    alertMessage?: string,

    /**
     * Der Typ der Meldung.
     */
    alertType?: AlertType
}

export default class RulesDialog extends React.Component<IProperties, IState>
{
    private _initialState: IState;
    private _isSaveButtonDisabled: boolean;

    /**
     * Erstellt eine neue Instanz der Klasse @see RulesDialog.
     * @param props Die Eigenschaften dieser Komponente.
     */
    constructor(props: IProperties)
    {
        super(props);

        // Modus ermitteln.
        var mode: Mode = Mode.create;
        if (props.rule)
        {
            mode = Mode.edit;
        }

        this.state = {
            isLoading: true,
            mode: mode,
            form: {
                calendar: {},
                calendars: []
            },
            errors: {}
        };

        this.initAsync(props);
    }

    /**
     * Lädt alle benötigten Daten und/oder weißt diese zu.
     * @param props Alle gesetzten Eigenschaften.
     */
    private async initAsync(props: IProperties)
    {
        if (this.state.mode === Mode.edit)
        {
            await this.initEditModeAsync(props);
        }
        else if (this.state.mode === Mode.create)
        {
            await this.initCreateModeAsync(props);
        }
    }

    /**
     * Initialisert den Edit Mode.
     * @param props Alle gesetzten Eigenschaften.
     */
    private async initEditModeAsync(props: IProperties)
    {
        var calendars: modelsGet.ICalendar[] = await apiCalendars.getCalendars();

        this.setState((state: IState) =>
        {
            state.isLoading = false;
            state.errors = {};
            state.alertMessage = null;
            state.alertType = null;
            state.rule = props.rule;
            state.calendars = calendars;

            state.form.calendars = props.rule.calendars.select(c => c);

            return state;
        }, () => this._initialState = {
            ...this.state,
            form: {
                ...this.state.form
            }
        });
    }

    /**
     * Initialisert den Create Mode.
     * @param props Alle gesetzten Eigenschaften.
     */
    private async initCreateModeAsync(props: IProperties)
    {
        var calendars: modelsGet.ICalendar[] = await apiCalendars.getCalendars();

        this.setState(() =>
        {
            const newState: IState = {};
            newState.isLoading = false;
            newState.errors = {};
            newState.alertMessage = null;
            newState.alertType = null;
            newState.rule = props.rule;
            newState.calendars = calendars;

            return newState;
        }, () => this._initialState = {
            ...this.state,
            form: {
                ...this.state.form
            }
        });
    }

    /**
     * Wird ausgeführt, bevor gerendert wird.
     * Hier wird geprüft ob der Button zum erstellen oder speichern
     * freigschaltet werden soll.
     * @param nextProps Die neuen Eigenschaften.
     * @param nextState Der neue State.
     */
    componentWillUpdate(nextProps: IProperties, nextState: IState)
    {
        this._isSaveButtonDisabled = true;

        if (this._initialState != null)
        {
            const { form } = this._initialState;

            if (nextState.form.calendars.length >= 2)
            {
                this._isSaveButtonDisabled = false;
            }
        }
    }

    /**
     * Wird ausgeführt, bevor der Dialog sich schließt.
     */
    private onClosing = (refresh: boolean = false) =>
    {
        var closeDialog: boolean = true;

        if (!this._isSaveButtonDisabled)
            closeDialog = confirm("Es sind noch ungespeicherte Änderungen vorhanden. Wollen Sie den Dialog trotzdem schließen?");

        if (closeDialog)
        {
            const { onClose } = this.props;
            onClose && onClose(refresh);
            return true;
        }
    }

    /**
     * Ändert den geöffneten Datensatz oder erstellt einen neuen.
     */
    private onSaveOrCreateClick = async () =>
    {
        try
        {
            this.setState({
                isLoading: true
            });

            const { mode, rule, form } = this.state;

            // Neues POST Objekt erstellen.
            var timeGroups: modelsGet.ITimeGroup[] = await globals.getSettingAsync<modelsGet.ITimeGroup[]>(globals.SettingKey.rulesCalendarsTimeGroups, true);
            timeGroups.push({
                calendars: form.calendars
            });

            if (mode === Mode.edit)
            {
                timeGroups.removeAt(t => t.key === rule.key);
                await apiSettings.putTimeGroups(timeGroups);
            }
            else
            {
                await apiSettings.putTimeGroups(timeGroups);
            }

            // Fenster schließen.
            this._isSaveButtonDisabled = true;
            this.onClosing(true);
        }
        catch (e)
        {
            var errors = utils.getErrors(e);
            var alertMessage: string;

            if (errors != null)
            {
                for (let error in errors)
                {
                    switch (error)
                    {
                        case "error":
                        case "date_is_invalid":
                            alertMessage = errors[error];
                            break;
                        case "internal_server_error":
                            alertMessage = "Auf dem Server ist ein Fehler aufgetreten! Wenn der Fehler weiterhin auftritt, kontaktieren Sie Ihren Administrator oder Ihren Software Anbieter.";
                            break;
                    }
                }
            }

            this.setState(() =>
            {
                const newState: IState = {};
                newState.isLoading = false;
                newState.errors = errors;
                newState.alertMessage = alertMessage;
                newState.alertType = AlertType.error;
                return newState;
            });
        }
    }

    /**
     * Löscht den geöffneten Datensatz.
     */
    private onDeleteClick = async () =>
    {
        var deleteData = confirm("Wollen Sie den Datensatz wirklich löschen?");
        if (!deleteData)
            return;

        try
        {
            this.setState({
                isLoading: true
            });

            const { rule } = this.state;

            var timeGroups: modelsGet.ITimeGroup[] = await globals.getSettingAsync<modelsGet.ITimeGroup[]>(globals.SettingKey.rulesCalendarsTimeGroups, true);
            timeGroups.removeAt(t => t.key === rule.key);
            await apiSettings.putTimeGroups(timeGroups);

            // Fenster schließen.
            this._isSaveButtonDisabled = true;
            this.onClosing(true);
        }
        catch (e)
        {
            var errors = utils.getErrors(e);
            var alertMessage: string;

            if (errors != null)
            {
                for (let error in errors)
                {
                    switch (error)
                    {
                        case "error":
                        case "date_is_invalid":
                            alertMessage = errors[error];
                            break;
                        case "internal_server_error":
                            alertMessage = "Auf dem Server ist ein Fehler aufgetreten! Wenn der Fehler weiterhin auftritt, kontaktieren Sie Ihren Administrator oder Ihren Software Anbieter.";
                            break;
                    }
                }
            }

            this.setState(() =>
            {
                const newState: IState = {};
                newState.isLoading = false;
                newState.errors = errors;
                newState.alertMessage = alertMessage;
                newState.alertType = AlertType.error;
                return newState;
            });
        }
    }

    /**
     * Wird ausgeführt, sobald sich der Wert einer ComboBox ändert.
     * @param selectedItem Der ausgewählte Datensatz.
     */
    private onSelectionChanged = (selectedItem: any, formKey: string, valueKey: string, selectedValueKey: string) =>
    {
        this.setState((state: IState) =>
        {
            state.form[formKey] = {
                value: selectedItem ? selectedItem[valueKey] : null,
                selectedValue: selectedItem ? selectedItem[selectedValueKey] : null
            };
            return state;
        });
    }

    /**
     * Wird ausgeführt, sobald eine Sperre hinzugefügt werden soll.
     */
    private onAddCalendar = () =>
    {
        this.setState((state: IState) =>
        {
            var calendars: number[] = (state.form.calendars && state.form.calendars.select(c => c)) || [];
            state.form.calendars.push(state.form.calendar.selectedValue);
            state.form.calendar = {};
            return state;
        });
    }

    /**
     * Wird ausgeführt, sobald eine Sperre entfernt werden soll.
     */
    private onRemoveCalendar = (calendar: modelsGet.ICalendar) =>
    {
        this.setState((state: IState) =>
        {
            state.form.calendars.remove(calendar.id);
            return state;
        });
    }

    /**
     * Rendert das Formular/den Content Bereich.
     */
    private renderContent(): JSX.Element
    {
        const {
            mode,
            rule,
            calendars,
            form,
            errors,
            alertMessage,
            alertType
        } = this.state;

        return (
            <div className="settings_dialog_content">
                <ScrollViewer
                    HorizontalScrollBarVisibility={ScrollBarVisibility.hidden}
                    VerticalScrollBarVisibility={ScrollBarVisibility.auto}
                    style={{
                        padding: 10
                    }}>
                    <FormGroup>
                        <ComboBox
                            label="Kalender"
                            canRemoveSelection={true}
                            dataSource={calendars.where(c => !form.calendars.any(c2 => c2 === c.id))}
                            dropDownMenuItemHeight={DROPDOWN_TEXT_ITEM_HEIGHT}
                            comboBoxType={ComboBoxType.dropDownList}
                            value={form.calendar.value}
                            selectedValue={form.calendar.selectedValue}
                            onSelectionChanged={(data) => this.onSelectionChanged(data, "calendar", "name", "id")}
                            valueMember="id"
                            displayMember="name"
                            size={InputSize.normal}>
                            {
                                (item: modelsGet.ICalendar) =>
                                {
                                    return (
                                        <DropDownTextItem
                                            key={item.id}
                                            value={item.id}
                                            displayValue={item.name} />
                                    );
                                }
                            }
                        </ComboBox>
                        <Button
                            style={{
                                marginBottom: 10
                            }}
                            text="Kalender hinzufügen"
                            type={ButtonType.default}
                            isDisabled={form.calendar.selectedValue == null}
                            onClick={this.onAddCalendar} />
                        {
                            form.calendars && form.calendars.length > 0 &&
                            <div className="time-groups_items">
                                {
                                    form.calendars.map((id) =>
                                    {
                                        let calendar: modelsGet.ICalendar = this.state.calendars.where(c => c.id === id).first();

                                        return (
                                            <div className="time-groups_items_item">
                                                <span className="time-groups_items_item_text">
                                                    {calendar.name}
                                                </span>
                                                <a
                                                    className="time-groups_items_item_remove"
                                                    onClick={() => this.onRemoveCalendar(calendar)}>Entfernen</a>
                                            </div>
                                        );
                                    })
                                }
                            </div>
                        }
                        {
                            form.calendars == null || form.calendars.length === 0 &&
                            <div className="time-groups_no-items">
                                Es müssen mindestens zwei Kalender hinzugefügt werden.
                            </div>
                        }
                    </FormGroup>
                </ScrollViewer>
            </div>
        );
    }

    /**
     * Rendert diese Komponente.
     */
    public render(): JSX.Element
    {
        const {
            isLoading,
            mode,
            rule,
            form,
            errors,
            alertMessage,
            alertType
        } = this.state;

        var icon: string = "loading";
        var title: string = "Daten werden geladen...";
        var buttonHeader: string = "";

        if (!isLoading)
        {
            if (mode === Mode.create)
            {
                icon = "plus";
                title = "Neue Regel erstellen";
                buttonHeader = "Erstellen";
            }
            else
            {
                icon = "pencil";
                title = "Regel bearbeiten";
                buttonHeader = "Speichern";
            }
        }

        return (
            <Window
                isOpen={true}
                titleIcon={icon}
                title={title}
                onClosing={this.onClosing}
                isMaximized={false}
                width={600}
                height={700}
                onEnterKeyUp={this.onSaveOrCreateClick}
                onEscapeKeyUp={() => this.onClosing(false)}>
                <div className="settings_dialog">
                    <Alert
                        style={utils.getGridChildStyle(1, 1, 1, 1)}
                        alertType={alertType}
                        isVisible={alertMessage ? true : false}
                        isClosable={true}
                        onHide={() => this.setState({ alertMessage: null, alertType: null })}>
                        {alertMessage}
                    </Alert>
                    {isLoading ? <Loader loaderStyle={LoaderStyle.style1} /> : this.renderContent()}
                    <div className="settings_dialog_actions">
                        {
                            mode === Mode.edit &&
                            <Button
                                text="Löschen"
                                type={ButtonType.danger}
                                onClick={this.onDeleteClick} />
                        }
                        <div style={{ flexGrow: 1 }}></div>
                        <Button
                            text={buttonHeader}
                            type={ButtonType.primary}
                            isDisabled={this._isSaveButtonDisabled}
                            onClick={this.onSaveOrCreateClick} />
                        <Button
                            text="Abbrechen"
                            type={ButtonType.default}
                            onClick={() => this.onClosing(false)} />
                    </div>
                </div>
            </Window>
        );
    }
}